import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {firstValueFrom, timeout} from 'rxjs';
import {buildApiUrl} from '../shared/api/api-url';
import {PushInstallation} from './push-installation';
import {PushRevocationDto} from './push-revocation-dto';
import {AuthSecureStorage} from '../auth/auth-refresh-token.storage';
import {isPushSupported, PushNative} from './push-native';

export const pushStorage = {
  get: (options: {key: string}) => AuthSecureStorage.get(options),
  set: (options: {key: string; value: string}) => AuthSecureStorage.set(options),
};
const key = 'push-installation-v1';
const preferencesKey = 'push-account-preferences-v1';
const permissionKey = 'push-permission-requested-v1';

@Injectable({providedIn: 'root'})
export class PushLocalStateService {
  private mutations: Promise<unknown> = Promise.resolve();
  private sessionEnded = false;

  constructor(private readonly http: HttpClient) {}

  private mutate<T>(operation: () => Promise<T>): Promise<T> {
    const result = this.mutations.then(operation);
    this.mutations = result.catch(() => undefined);
    return result;
  }

  async read(): Promise<PushInstallation | null> {
    if (!isPushSupported()) return null;
    const saved = await pushStorage.get({key});
    return saved.value ? JSON.parse(saved.value) as PushInstallation : null;
  }

  prepareAccount(userId: string): Promise<void> {
    return this.mutate(() => this.prepareAccountCore(userId));
  }

  private async prepareAccountCore(userId: string): Promise<void> {
    if (!isPushSupported()) return;
    const previous = await this.read();
    const preferences = await this.readPreferences();
    // Legacy false cannot distinguish initial opt-in from a deliberate disable.
    if (previous && preferences[previous.userId] === undefined) preferences[previous.userId] = previous.enabled;
    if (preferences[userId] === undefined) preferences[userId] = true;
    await pushStorage.set({key: preferencesKey, value: JSON.stringify(preferences)});
    if (previous?.userId === userId) return;
    await this.disableCore();
    const bytes = crypto.getRandomValues(new Uint8Array(32));
    await this.save({
      installationId: previous?.installationId ?? crypto.randomUUID(),
      installationSecret: previous?.installationSecret ?? Array.from(bytes, b => b.toString(16).padStart(2, '0')).join(''),
      bindingId: crypto.randomUUID(), userId, enabled: false,
    });
  }

  enable(userId: string): Promise<PushInstallation | null> {
    return this.mutate(async () => {
      if (this.sessionEnded) return null;
      await this.prepareAccountCore(userId);
      await this.setPreference(userId, true);
      return this.enableCore();
    });
  }

  activate(userId: string): Promise<PushInstallation | null> {
    return this.mutate(async () => {
      const current = await this.read();
      if (this.sessionEnded || current?.userId !== userId) return null;
      return await this.wantsPush(userId) ? this.enableCore() : null;
    });
  }

  startSession(): void { this.sessionEnded = false; }

  async wantsPush(userId: string): Promise<boolean> {
    return (await this.readPreferences())[userId] !== false;
  }

  async shouldRequestPermission(): Promise<boolean> {
    return (await pushStorage.get({key: permissionKey})).value !== 'true';
  }

  markPermissionRequested(): Promise<void> {
    return pushStorage.set({key: permissionKey, value: 'true'});
  }

  private async readPreferences(): Promise<Record<string, boolean>> {
    const saved = await pushStorage.get({key: preferencesKey});
    return saved.value ? JSON.parse(saved.value) as Record<string, boolean> : {};
  }

  private async setPreference(userId: string, enabled: boolean): Promise<void> {
    const preferences = await this.readPreferences();
    preferences[userId] = enabled;
    await pushStorage.set({key: preferencesKey, value: JSON.stringify(preferences)});
  }

  private async enableCore(): Promise<PushInstallation> {
    const previous = await this.read();
    if (!previous) throw new Error('Push installation unavailable.');
    const active = {...previous, enabled: true, bindingId: previous.enabled ? previous.bindingId : crypto.randomUUID()};
    await this.save(active);
    await PushNative.setBinding({bindingId: active.bindingId});
    return active;
  }

  async disable(userId?: string, reason: 'logout' | 'preference' | 'permission' = 'logout'): Promise<void> {
    if (!isPushSupported()) return;
    if (reason === 'logout') this.sessionEnded = true;
    await PushNative.clearBinding();
    const previous = await this.mutate(async () => {
      if (reason === 'preference' && userId) await this.setPreference(userId, false);
      return this.disableCore();
    });
    if (!previous || previous.userId !== userId) return;
    const request: PushRevocationDto = {installationId: previous.installationId, bindingId: previous.bindingId};
    try {
      await firstValueFrom(this.http.post(buildApiUrl('/v1/push/registrations/deactivate'), request).pipe(timeout(5000)));
    } catch {
      // Local logout succeeds. Server deactivation requires a later authenticated logout.
    }
  }

  private async disableCore(): Promise<PushInstallation | null> {
    if (!isPushSupported()) return null;
    // This is independent of auth state and happens before any network operation.
    await PushNative.clearBinding();
    const previous = await this.read();
    if (previous?.enabled) await this.save({...previous, enabled: false});
    return previous;
  }

  private save(value: PushInstallation): Promise<void> {
    return pushStorage.set({key, value: JSON.stringify(value)});
  }

}
